import React, { useState, createContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { useCustomer } from 'lib/core/hooks'
import { RecommendationsResult } from 'hooks/productSelector'
import { Container, Heading, Content } from './styled'
import { getCustomerRecommendation } from 'services/productSelectorData'
import { PRODUCT_SELECTOR_CODE } from 'app/livbrite/resources/constants'
import { ReactComponent as ArrowSvg } from 'resources/svg/icons/caret-down.svg'
import { Spinner } from 'lib/ui'

export const CustomerResults = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { results, loading } = useRecommendationContext()

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {results && results?.length > 0 && (
            <Container>
              <Heading
                onClick={() => setIsOpen(!isOpen)}
                isOpen={isOpen}
              >
                See My Recommendations
                <ArrowSvg />
              </Heading>
              {isOpen && (
                <Content>
                  {results?.map(result => {
                    const { name, learn_more: learnMore, view_related_products: relatedProducts } = result

                    return (
                      <div key={name}>
                        <h2>{name}</h2>
                        <Link to={learnMore ?? '/'}>Learn More</Link>
                        <Link to={relatedProducts ?? '/'}>Find Products</Link>
                      </div>
                    )
                  })}
                </Content>
              )}
            </Container>
          )}
        </>
      )}
    </>
  )
}

interface RecommendationContextInterface {
  results?: RecommendationsResult[]
  setResults: (param: RecommendationsResult[]) => void
  fetchRecommendations: () => void
  loading: boolean
}

export const RecommendationContext = createContext<RecommendationContextInterface>({
  results: [],
  setResults: (param) => undefined,
  fetchRecommendations: () => undefined,
  loading: true
})

export const RecommendationProvider: React.FC = ({ children }) => {
  const { customer } = useCustomer()
  const [results, setResults] = useState<RecommendationsResult[] | undefined>()
  const [fetchRecommendations, { loading, data }] = useLazyQuery(getCustomerRecommendation, {
    variables: {
      customerEmail: customer?.email,
      questionnaireCode: PRODUCT_SELECTOR_CODE.SURVEY
    },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    fetchRecommendations()
  }, [fetchRecommendations])

  useEffect(() => {
    const results = data?.psGetRecommendation?.products
    if (results) {
      setResults(results)
    }
  }, [loading, data])

  return (
    <RecommendationContext.Provider
      value={{
        results,
        setResults,
        fetchRecommendations,
        loading
      }}
    >
      {children}
    </RecommendationContext.Provider>
  )
}

export const useRecommendationContext = () => {
  const context = React.useContext(RecommendationContext)
  if (context === undefined) {
    throw new Error('useRecommendation must be used within a RecommendationProvider')
  }
  return context
}
